import { useState, useEffect, useCallback } from "react";
import {
    PhoneCall, Loader2, RefreshCw, KeyRound, AlertTriangle, CheckCircle, Copy,
    Trash2, PhoneIncoming, Link2, Users, Plug, Save, History, FileText, Download,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import {
    TELEPHONY_CONFIG_URL, TELEPHONY_TEST_URL, TELEPHONY_ROTATE_KEY_URL,
    TELEPHONY_AGENTS_URL, TELEPHONY_SYNC_URL, TELEPHONY_TRANSCRIBE_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

const PROVIDERS = [
    { key: "twilio",   label: "Twilio" },
    { key: "smartflo", label: "Tata Tele Smartflo" },
];

const DEFAULT_SMARTFLO_DOMAIN = "https://api-smartflo.tatateleservices.com";

// Sarvam speech-to-text languages; 'unknown' lets Sarvam detect it per call.
const TRANSCRIBE_LANGUAGES = [
    ["unknown", "Auto-detect"], ["en-IN", "English"], ["hi-IN", "Hindi"], ["ta-IN", "Tamil"],
    ["ml-IN", "Malayalam"], ["te-IN", "Telugu"], ["kn-IN", "Kannada"], ["mr-IN", "Marathi"],
    ["bn-IN", "Bengali"], ["gu-IN", "Gujarati"], ["pa-IN", "Punjabi"], ["od-IN", "Odia"],
];
const TRANSCRIBE_MODES = [
    ["transcribe", "Original language"],
    ["translate",  "English translation"],
    ["codemix",    "Mixed (English words kept)"],
    ["translit",   "Romanised"],
];
const SYNC_DAYS = [1, 7, 30, 60, 90];
const TRANSCRIBE_DAYS = [7, 30, 90];

// Plain fields edited in the form. Secrets are kept apart: the API only ever
// returns a masked preview, and a blank secret means "keep the stored one".
const PLAIN_FIELDS = [
    "provider", "twilio_account_sid", "twilio_phone_number",
    "smartflo_api_domain", "smartflo_caller_id", "smartflo_transfer_intercom",
    "smartflo_login_email",
    "auto_transcribe", "transcribe_language", "transcribe_mode", "transcribe_min_seconds",
];
const SECRET_FIELDS = ["twilio_auth_token", "smartflo_api_token", "smartflo_ai_c2c_key", "smartflo_login_password"];

const Section = ({ icon: Icon, title, subtitle, children, action }) => (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <h2 className="text-sm font-semibold text-foreground">{title}</h2>
                    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {action}
        </div>
        {children}
    </div>
);

const Toggle = ({ checked, onChange, disabled, label, hint }) => (
    <div className={cn("flex items-start gap-3", disabled && "opacity-50")}>
        <button type="button" role="switch" aria-checked={!!checked} disabled={disabled}
            onClick={() => onChange(!checked)}
            className={cn("relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
                checked ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600")}>
            <span className={cn("inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform",
                checked ? "translate-x-[18px]" : "translate-x-[3px]")} />
        </button>
        <span>
            <span className="block text-xs font-medium text-foreground">{label}</span>
            {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
        </span>
    </div>
);

const Field = ({ label, hint, children }) => (
    <div className="space-y-1">
        <label className="text-xs font-medium text-foreground">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);

// Read-only URL with a copy button — same idea as the Zapier webhook page.
const CopyField = ({ label, value, hint }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="space-y-1">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</label>
            <div className="flex items-center gap-1 bg-muted/50 border border-border rounded-lg px-3 py-2">
                <span className="flex-1 text-xs font-mono text-foreground break-all select-all">
                    {value || "—"}
                </span>
                <button onClick={copy} title="Copy to clipboard" disabled={!value}
                    className="ml-2 p-1 rounded text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
                    {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </button>
            </div>
            {hint}
        </div>
    );
};

const code = (text) => (
    <code className="bg-muted px-1 rounded font-mono">{text}</code>
);

const toForm = (cfg) => {
    const f = {};
    PLAIN_FIELDS.forEach((k) => { f[k] = cfg?.[k] ?? ""; });
    if (!f.provider) f.provider = "twilio";
    if (!f.smartflo_api_domain) f.smartflo_api_domain = DEFAULT_SMARTFLO_DOMAIN;
    f.auto_transcribe = !!cfg?.auto_transcribe;
    if (!f.transcribe_language) f.transcribe_language = "unknown";
    if (!f.transcribe_mode) f.transcribe_mode = "transcribe";
    // Held as text while typing; sent as a whole number.
    f.transcribe_min_seconds = String(cfg?.transcribe_min_seconds ?? "");
    return f;
};

const EMPTY_SECRETS = {
    twilio_auth_token: "", smartflo_api_token: "", smartflo_ai_c2c_key: "", smartflo_login_password: "",
};
const EMPTY_AGENT = { user_id: "", agent_number: "", smartflo_agent_id: "", receives_inbound: true };

export default function Telephony() {
    const { fetch, create, update, remove } = useApiMethods();

    const [config, setConfig] = useState(null);
    const [agents, setAgents] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");

    const [form, setForm] = useState(toForm(null));
    const [secrets, setSecrets] = useState(EMPTY_SECRETS);
    const [testResult, setTestResult] = useState(null);
    const [confirmRotate, setConfirmRotate] = useState(false);
    const [newAgent, setNewAgent] = useState(EMPTY_AGENT);
    const [syncDays, setSyncDays] = useState(1);
    const [transcribeDays, setTranscribeDays] = useState(7);
    // An organisation runs on one provider. Once chosen, the other is hidden
    // until someone deliberately asks to change it.
    const [changingProvider, setChangingProvider] = useState(false);

    const applyConfig = (cfg) => {
        setConfig(cfg);
        setForm(toForm(cfg));
        setSecrets(EMPTY_SECRETS);
        setChangingProvider(false);
    };

    const load = useCallback(() => {
        setLoading(true);
        fetch(TELEPHONY_CONFIG_URL)
            .then((res) => {
                if (res?.status === "success") {
                    applyConfig(res.data);
                    setAgents(res.agents || []);
                    setUsers(res.users || []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load telephony settings");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load telephony settings"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // PUT a patch; used both by the Save button and by the on/off switches,
    // which take effect immediately.
    const put = (patch, key = "save") => {
        setBusy(key);
        return update(TELEPHONY_CONFIG_URL, patch)
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Saved");
                    applyConfig(res.data);
                } else ToastMsg("error", res?.message || "Save failed");
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Save failed"))
            .finally(() => setBusy(""));
    };

    const changedPatch = () => {
        // Compared with the normalised config, so the prefilled default domain
        // does not count as an edit on its own.
        const saved = toForm(config);
        const patch = {};
        PLAIN_FIELDS.forEach((k) => {
            if (form[k] !== saved[k]) patch[k] = typeof form[k] === "string" ? form[k].trim() : form[k];
        });
        if ("transcribe_min_seconds" in patch) {
            patch.transcribe_min_seconds = Math.min(600, Math.max(0, parseInt(patch.transcribe_min_seconds, 10) || 0));
        }
        SECRET_FIELDS.forEach((k) => { if (secrets[k].trim()) patch[k] = secrets[k].trim(); });
        return patch;
    };
    const dirty = config ? Object.keys(changedPatch()).length > 0 : false;

    const save = () => {
        const patch = changedPatch();
        if (!Object.keys(patch).length) return;
        // First Smartflo save: persist the prefilled domain along with the rest.
        if (form.provider === "smartflo" && !config.smartflo_api_domain) {
            patch.smartflo_api_domain = form.smartflo_api_domain.trim();
        }
        put(patch);
    };

    const test = () => {
        setBusy("test");
        setTestResult(null);
        create(TELEPHONY_TEST_URL, {})
            .then((res) => setTestResult({
                ok: res?.status === "success",
                message: res?.message || (res?.status === "success" ? "Connected" : "Connection failed"),
            }))
            .catch((err) => setTestResult({ ok: false, message: err?.response?.data?.message || "Connection failed" }))
            .finally(() => setBusy(""));
    };

    const rotate = () => {
        setConfirmRotate(false);
        setBusy("rotate");
        create(TELEPHONY_ROTATE_KEY_URL, {})
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Key rotated — re-paste every URL in the provider portal");
                    applyConfig(res.data);
                } else ToastMsg("error", res?.message || "Could not rotate the key");
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Could not rotate the key"))
            .finally(() => setBusy(""));
    };

    // One day runs inline and reports counts; longer ranges run in the
    // background and only say they have started.
    const sync = () => {
        setBusy("sync");
        create(TELEPHONY_SYNC_URL, { days: syncDays })
            .then((res) => {
                ToastMsg(res?.status === "success" ? "success" : "error", res?.message || "Import failed");
                if (res?.status === "success") load();
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Import failed"))
            .finally(() => setBusy(""));
    };

    const transcribePast = () => {
        setBusy("transcribe");
        create(TELEPHONY_TRANSCRIBE_URL, { days: transcribeDays })
            .then((res) => ToastMsg(res?.status === "success" ? "success" : "error",
                res?.message || "Could not queue transcripts"))
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Could not queue transcripts"))
            .finally(() => setBusy(""));
    };

    // ── Staff numbers ────────────────────────────────────────────────────────
    const saveAgent = (row, key = "agent") => {
        setBusy(key);
        return create(TELEPHONY_AGENTS_URL, {
            user_id: row.user_id,
            agent_number: (row.agent_number || "").trim(),
            smartflo_agent_id: (row.smartflo_agent_id || "").trim(),
            receives_inbound: !!row.receives_inbound,
        })
            .then((res) => {
                if (res?.status === "success") {
                    setAgents(res.data || []);
                    return true;
                }
                ToastMsg("error", res?.message || "Could not save the number");
                return false;
            })
            .catch((err) => { ToastMsg("error", err?.response?.data?.message || "Could not save the number"); return false; })
            .finally(() => setBusy(""));
    };

    const addAgent = () => {
        // Smartflo can ring an agent by its agent id alone; Twilio needs a phone.
        const onSmartflo = form.provider === "smartflo";
        const hasRing = newAgent.agent_number.trim()
            || (onSmartflo && newAgent.smartflo_agent_id.trim());
        if (!newAgent.user_id || !hasRing) {
            return ToastMsg("error", onSmartflo
                ? "Pick a user and enter their phone number or Smartflo agent ID"
                : "Pick a user and enter the phone number to ring");
        }
        saveAgent(newAgent).then((ok) => {
            if (ok) { ToastMsg("success", "Number saved"); setNewAgent(EMPTY_AGENT); }
        });
    };

    const dropAgent = (a) => {
        if (!window.confirm(`Remove ${a.user_name}'s number?`)) return;
        setBusy(`del-${a.id}`);
        remove(TELEPHONY_AGENTS_URL, { id: a.id })
            .then((res) => {
                if (res?.status === "success") setAgents(res.data || []);
                else ToastMsg("error", res?.message || "Could not remove the number");
            })
            .catch(() => ToastMsg("error", "Could not remove the number"))
            .finally(() => setBusy(""));
    };

    const pickUser = (id) => {
        const u = users.find((x) => String(x.id) === String(id));
        setNewAgent((a) => ({ ...a, user_id: id, agent_number: u?.phone || a.agent_number }));
    };

    if (loading && !config) {
        return (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }
    if (!config) return null;

    const isSmartflo = form.provider === "smartflo";
    // "Chosen" once calling is on or the saved provider has credentials. A
    // fresh row defaults to Twilio with nothing set — that is not a choice yet.
    const providerChosen = config.is_enabled || (config.provider === "smartflo"
        ? !!(config.smartflo_login_email || config.smartflo_api_token_preview)
        : !!(config.twilio_account_sid || config.twilio_auth_token_preview));
    const hooks = config.webhooks || {};
    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setS = (k, v) => setSecrets((s) => ({ ...s, [k]: v }));
    const secretPlaceholder = (preview) => (preview ? `${preview} — leave blank to keep` : "Not set");

    return (
        <div className="space-y-5">
            {/* ── Provider ── */}
            <Section icon={PhoneCall} title="Calling provider"
                subtitle="Staff click Call on a lead or enquiry; the provider rings their phone, then connects the customer. Every call is logged against the record."
                action={
                    <Button size="sm" variant="outline" onClick={test} disabled={!!busy}>
                        {busy === "test" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <Plug className="h-3.5 w-3.5 mr-1.5" />}
                        Test connection
                    </Button>
                }>
                {providerChosen && !changingProvider ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground">
                            {PROVIDERS.find((p) => p.key === config.provider)?.label || config.provider}
                            <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                                Active
                            </span>
                        </span>
                        <button type="button" onClick={() => setChangingProvider(true)}
                            className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground">
                            Change provider
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
                                {PROVIDERS.map((p) => (
                                    <button key={p.key} type="button" onClick={() => setF("provider", p.key)}
                                        className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                            form.provider === p.key
                                                ? "bg-background text-foreground shadow-sm"
                                                : "text-muted-foreground hover:text-foreground")}>
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            {changingProvider && (
                                <button type="button"
                                    onClick={() => { setF("provider", config.provider); setChangingProvider(false); }}
                                    className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground">
                                    Cancel
                                </button>
                            )}
                        </div>
                        {changingProvider && (
                            <p className="text-[11px] text-amber-700 dark:text-amber-300 -mt-2">
                                Switching moves every call — staff click-to-call and the AI agent — to the new
                                provider. Calls already made keep their recordings.
                            </p>
                        )}
                        {form.provider !== config.provider && (
                            <p className="text-[11px] text-amber-700 dark:text-amber-300 -mt-2">
                                Not switched yet — fill in the credentials below and press Save.
                            </p>
                        )}
                    </>
                )}

                <div className="flex flex-wrap gap-6">
                    <Toggle checked={config.is_enabled} disabled={!!busy}
                        onChange={(v) => put({ is_enabled: v }, "toggle")}
                        label="Calling enabled"
                        hint="When off, Call buttons refuse to dial." />
                    <Toggle checked={config.record_calls} disabled={!!busy}
                        onChange={(v) => put({ record_calls: v }, "toggle")}
                        label="Record calls"
                        hint="Recordings play back from the call history on each record." />
                </div>

                {testResult && (
                    <div className={cn("flex items-start gap-2 rounded-md p-2 text-xs",
                        testResult.ok
                            ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                            : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300")}>
                        {testResult.ok ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                        <span className="break-all">
                            {testResult.message}
                            {dirty && " (tested the saved settings — unsaved changes were not included)"}
                        </span>
                    </div>
                )}

                {config.last_error && (
                    <div className="flex items-start gap-2 rounded-md p-2 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span className="break-all">Last error: {config.last_error}</span>
                    </div>
                )}
            </Section>

            {/* ── Credentials ── */}
            <Section icon={KeyRound} title={`${isSmartflo ? "Smartflo" : "Twilio"} credentials`}
                subtitle="Secrets are never shown again once saved. Leave a secret blank to keep the stored one."
                action={
                    <Button size="sm" onClick={save} disabled={!!busy || !dirty}>
                        {busy === "save" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <Save className="h-3.5 w-3.5 mr-1.5" />}
                        Save
                    </Button>
                }>
                {!isSmartflo ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Account SID">
                            <Input value={form.twilio_account_sid} placeholder="AC…"
                                onChange={(e) => setF("twilio_account_sid", e.target.value)}
                                className="font-mono text-xs" />
                        </Field>
                        <Field label="Auth token">
                            <Input type="password" autoComplete="new-password" value={secrets.twilio_auth_token}
                                placeholder={secretPlaceholder(config.twilio_auth_token_preview)}
                                onChange={(e) => setS("twilio_auth_token", e.target.value)}
                                className="font-mono text-xs" />
                        </Field>
                        <Field label="Phone number" hint="The Twilio number customers see, in +E.164 form.">
                            <Input value={form.twilio_phone_number} placeholder="+91…"
                                onChange={(e) => setF("twilio_phone_number", e.target.value)}
                                className="text-sm" />
                        </Field>
                        <p className="text-[11px] text-muted-foreground md:col-span-2">
                            Leave all three blank to use the platform's shared Twilio account.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="API domain">
                            <Input value={form.smartflo_api_domain}
                                onChange={(e) => setF("smartflo_api_domain", e.target.value)}
                                className="font-mono text-xs" />
                        </Field>
                        <Field label="API token">
                            <Input type="password" autoComplete="new-password" value={secrets.smartflo_api_token}
                                placeholder={secretPlaceholder(config.smartflo_api_token_preview)}
                                onChange={(e) => setS("smartflo_api_token", e.target.value)}
                                className="font-mono text-xs" />
                        </Field>
                        <Field label="Smartflo login email">
                            <Input value={form.smartflo_login_email} autoComplete="off"
                                onChange={(e) => setF("smartflo_login_email", e.target.value)}
                                className="text-sm" />
                        </Field>
                        <Field label="Smartflo login password">
                            <Input type="password" autoComplete="new-password" value={secrets.smartflo_login_password}
                                placeholder={secretPlaceholder(config.smartflo_login_password_preview)}
                                onChange={(e) => setS("smartflo_login_password", e.target.value)}
                                className="font-mono text-xs" />
                        </Field>
                        <p className="text-[11px] text-muted-foreground md:col-span-2 -mt-2">
                            Use either a permanent API token (ask Smartflo support for one) or your Smartflo
                            login — with the login saved, the CRM fetches a new 1-hour token automatically.
                        </p>
                        <Field label="Caller ID / DID" hint="The number customers see when staff call out.">
                            <Input value={form.smartflo_caller_id}
                                onChange={(e) => setF("smartflo_caller_id", e.target.value)}
                                className="text-sm" />
                        </Field>
                        <Field label="AI Click-to-Call key"
                            hint="Click-to-Call Support key whose destination is the Voice Bot — used for the AI agent's outbound calls.">
                            <Input type="password" autoComplete="new-password" value={secrets.smartflo_ai_c2c_key}
                                placeholder={secretPlaceholder(config.smartflo_ai_c2c_key_preview)}
                                onChange={(e) => setS("smartflo_ai_c2c_key", e.target.value)}
                                className="font-mono text-xs" />
                        </Field>
                        <Field label="Transfer intercom"
                            hint="Where a live AI call is handed to a human — an agent, department or IVR intercom in Smartflo.">
                            <Input value={form.smartflo_transfer_intercom}
                                onChange={(e) => setF("smartflo_transfer_intercom", e.target.value)}
                                className="text-sm" />
                        </Field>
                    </div>
                )}
            </Section>

            {/* ── Incoming calls ── */}
            <Section icon={PhoneIncoming} title="Incoming calls"
                subtitle="What happens when a customer rings your number.">
                {!isSmartflo ? (
                    <div className="flex flex-col gap-2">
                        {[["ai", "AI voice agent answers", "The AI caller picks up, qualifies and logs the call."],
                          ["agents", "Ring staff phones", "Rings the staff numbers below marked to receive incoming calls."]]
                            .map(([val, label, hint]) => (
                                <label key={val} className="flex items-start gap-2 cursor-pointer">
                                    <input type="radio" name="inbound_mode" value={val}
                                        checked={config.inbound_mode === val} disabled={!!busy}
                                        onChange={() => put({ inbound_mode: val }, "toggle")}
                                        className="mt-0.5" />
                                    <span>
                                        <span className="block text-xs font-medium text-foreground">{label}</span>
                                        <span className="block text-[11px] text-muted-foreground">{hint}</span>
                                    </span>
                                </label>
                            ))}
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground">
                        Smartflo routes incoming calls itself — set it up in the Smartflo portal: point
                        the IVR at your agents to ring staff, or at the Voice Bot to have the AI agent
                        answer. Every call still lands here through the webhook below.
                    </p>
                )}
            </Section>

            {/* ── Provider URLs ── */}
            <Section icon={Link2} title="Paste these into your provider"
                subtitle="Each URL carries a secret key. Anyone with it can post call events to this account."
                action={!confirmRotate && (
                    <Button size="sm" variant="outline" onClick={() => setConfirmRotate(true)} disabled={!!busy}>
                        {busy === "rotate" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        Rotate key
                    </Button>
                )}>
                {hooks.base_url_configured === false && (
                    <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                        <span>
                            The server's public base URL ({code("AI_CALLER_WEBHOOK_BASE_URL")}) is not set,
                            so these URLs may not be reachable from the provider. Ask your administrator to set it.
                        </span>
                    </div>
                )}

                {confirmRotate && (
                    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 space-y-2">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-red-700 dark:text-red-300">
                                <strong>The current URLs stop working immediately.</strong> Every URL in the
                                provider portal must be re-pasted.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="destructive" size="sm" onClick={rotate}>Yes, rotate key</Button>
                            <Button variant="outline" size="sm" onClick={() => setConfirmRotate(false)}>Cancel</Button>
                        </div>
                    </div>
                )}

                {isSmartflo ? (
                    <>
                        <CopyField label="Webhook URL" value={hooks.smartflo_webhook}
                            hint={
                                <ol className="text-[11px] text-muted-foreground space-y-0.5 list-decimal pl-4">
                                    <li>Smartflo portal → <strong>API Connect</strong> → <strong>Webhook</strong> → <strong>Add webhook</strong></li>
                                    <li>Method {code("POST")}, Content-Type {code("JSON")}, URL above</li>
                                    <li>
                                        Add one webhook for each trigger, for both <strong>Inbound</strong> and <strong>Outbound</strong>:
                                        <ul className="list-disc pl-4 mt-0.5">
                                            <li>Call hangup (Missed or Answered)</li>
                                            <li>Call answered by Agent</li>
                                            <li>Call answered by Customer (Click to Call)</li>
                                            <li>Call missed by Customer (Click to Call)</li>
                                        </ul>
                                    </li>
                                </ol>
                            } />
                        <CopyField label="Voice Bot dynamic endpoint" value={hooks.smartflo_voice_bot}
                            hint={
                                <p className="text-[11px] text-muted-foreground">
                                    Smartflo portal → <strong>Settings</strong> → <strong>Channels</strong> →{" "}
                                    <strong>Voice Bot</strong> → <strong>Add an Endpoint</strong> →{" "}
                                    <strong>Dynamic</strong>, method {code("POST")}. Needed only for the AI agent.
                                </p>
                            } />
                        {hooks.smartflo_voice_bot_wss && (
                            <CopyField label="Voice Bot static endpoint (wss)" value={hooks.smartflo_voice_bot_wss}
                                hint={
                                    <p className="text-[11px] text-muted-foreground">
                                        Use this instead of the dynamic one if the endpoint is set to{" "}
                                        <strong>Static URL</strong> — paste it as the WebSocket (wss) endpoint.
                                        Use one or the other, not both.
                                    </p>
                                } />
                        )}
                    </>
                ) : (
                    <CopyField label="Incoming call URL" value={hooks.twilio_incoming}
                        hint={
                            <p className="text-[11px] text-muted-foreground">
                                Twilio console → Phone Numbers → your number → Voice → <strong>A call comes in</strong>:
                                Webhook, this URL, {code("HTTP POST")}.
                            </p>
                        } />
                )}
            </Section>

            {/* ── Staff numbers ── */}
            <Section icon={Users} title="Staff numbers"
                subtitle="The phone each person is rung on when they press Call. A user without a number here cannot place calls.">
                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                            <tr className="text-muted-foreground">
                                <th className="px-3 py-2 text-left font-medium">User</th>
                                <th className="px-3 py-2 text-left font-medium">Number</th>
                                {isSmartflo && <th className="px-3 py-2 text-left font-medium">Smartflo agent id</th>}
                                <th className="px-3 py-2 text-center font-medium">Receives incoming</th>
                                <th className="px-3 py-2 w-10" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {agents.map((a) => (
                                <tr key={a.id} className="hover:bg-accent/30">
                                    <td className="px-3 py-2 text-foreground">{a.user_name}</td>
                                    <td className="px-3 py-2 text-muted-foreground font-mono">{a.agent_number}</td>
                                    {isSmartflo && (
                                        <td className="px-3 py-2 text-muted-foreground font-mono">{a.smartflo_agent_id || "—"}</td>
                                    )}
                                    <td className="px-3 py-2 text-center">
                                        <input type="checkbox" checked={!!a.receives_inbound} disabled={!!busy}
                                            onChange={(e) => saveAgent({ ...a, receives_inbound: e.target.checked })}
                                            className="rounded border-input" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <button onClick={() => dropAgent(a)} disabled={!!busy}
                                            className="text-muted-foreground hover:text-red-600 p-1">
                                            {busy === `del-${a.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : <Trash2 className="h-3.5 w-3.5" />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {agents.length === 0 && (
                                <tr><td colSpan={isSmartflo ? 5 : 4} className="px-3 py-6 text-center text-muted-foreground">
                                    No staff numbers yet — add one below.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <select value={newAgent.user_id} onChange={(e) => pickUser(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground min-w-[180px]">
                        <option value="">Select user…</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <Input value={newAgent.agent_number}
                        placeholder={isSmartflo ? "Phone number (optional)" : "Phone number to ring"}
                        onChange={(e) => setNewAgent((a) => ({ ...a, agent_number: e.target.value }))}
                        className="h-9 w-48 text-sm" />
                    {isSmartflo && (
                        <Input value={newAgent.smartflo_agent_id} placeholder="Smartflo agent ID"
                            onChange={(e) => setNewAgent((a) => ({ ...a, smartflo_agent_id: e.target.value }))}
                            className="h-9 w-52 text-sm" />
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={newAgent.receives_inbound}
                            onChange={(e) => setNewAgent((a) => ({ ...a, receives_inbound: e.target.checked }))}
                            className="rounded border-input" />
                        receives incoming
                    </label>
                    <Button size="sm" onClick={addAgent} disabled={!!busy}>
                        {busy === "agent" && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        Add
                    </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                    Saving a user who already has a number replaces it.
                </p>
            </Section>

            {/* ── Call transcripts ── */}
            <Section icon={FileText} title="Call transcripts"
                subtitle="Recorded calls turned into text by Sarvam AI — searchable from the Call Log and readable on each record."
                action={
                    <Button size="sm" onClick={save} disabled={!!busy || !dirty}>
                        {busy === "save" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <Save className="h-3.5 w-3.5 mr-1.5" />}
                        Save
                    </Button>
                }>
                {!config.transcription_available && (
                    <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                        <span>
                            Transcription is not available — the server has no Sarvam key
                            ({code("SARVAM_API_KEY")}). Ask your administrator to set it.
                        </span>
                    </div>
                )}

                <Toggle checked={form.auto_transcribe} disabled={!!busy}
                    onChange={(v) => setF("auto_transcribe", v)}
                    label="Transcribe answered calls automatically"
                    hint="Billed per minute by Sarvam AI." />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Language">
                        <select value={form.transcribe_language}
                            onChange={(e) => setF("transcribe_language", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground">
                            {TRANSCRIBE_LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </Field>
                    <Field label="Output">
                        <select value={form.transcribe_mode}
                            onChange={(e) => setF("transcribe_mode", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground">
                            {TRANSCRIBE_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            {/* Not offered, but kept selectable if it was set elsewhere. */}
                            {form.transcribe_mode === "verbatim" && <option value="verbatim">Verbatim</option>}
                        </select>
                    </Field>
                    <Field label="Skip calls shorter than">
                        <div className="flex items-center gap-2">
                            <Input type="number" min="0" max="600" value={form.transcribe_min_seconds}
                                onChange={(e) => setF("transcribe_min_seconds", e.target.value)}
                                className="h-9 w-24 text-sm" />
                            <span className="text-xs text-muted-foreground">seconds</span>
                        </div>
                    </Field>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
                    <span className="text-xs font-medium text-foreground">Transcribe past calls from the last</span>
                    <select value={transcribeDays} onChange={(e) => setTranscribeDays(Number(e.target.value))}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground">
                        {TRANSCRIBE_DAYS.map((d) => <option key={d} value={d}>{d} days</option>)}
                    </select>
                    <Button size="sm" variant="outline" onClick={transcribePast}
                        disabled={!!busy || !config.transcription_available}>
                        {busy === "transcribe" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                        Transcribe
                    </Button>
                    <span className="text-[11px] text-muted-foreground">
                        Answered calls with a recording and no transcript yet.
                    </span>
                </div>
            </Section>

            {/* ── Smartflo call-history import ── */}
            {isSmartflo && (
                <Section icon={History} title="Import past calls"
                    subtitle="Pulls calls from Smartflo's call records — history from before the CRM was connected, or any the webhook missed."
                    action={
                        <div className="flex items-center gap-2">
                            <select value={syncDays} onChange={(e) => setSyncDays(Number(e.target.value))}
                                className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground">
                                {SYNC_DAYS.map((d) => <option key={d} value={d}>Last {d} day{d > 1 ? "s" : ""}</option>)}
                            </select>
                            <Button size="sm" variant="outline" onClick={sync} disabled={!!busy}>
                                {busy === "sync" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    : <Download className="h-3.5 w-3.5 mr-1.5" />}
                                Import
                            </Button>
                        </div>
                    }>
                    <p className="text-xs text-muted-foreground">
                        Last synced: {config.last_synced_at || "never"}. More than a day runs in the
                        background — refresh the Call Log in a few minutes.
                    </p>
                </Section>
            )}
        </div>
    );
}
